import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Window-level paste plus drop-target-scoped drag-and-drop listeners for
 * attaching files to the chat prompt bar. The prompt bar is mounted exactly
 * when it is on screen, so mount scope doubles as "present and in view" — no
 * visibility tracking needed.
 *
 * Drag-and-drop only engages over `dropTargetRef` (the prompt bar). Other
 * drop targets in the chat — e.g. the reference-image card slots — keep
 * receiving their own drops. A window-level guard preventDefaults file drags
 * everywhere for the hook's whole lifetime — even while `enabled` is false
 * (mid-upload, read-only chat) — so a drop that misses every target can't
 * navigate the browser away from the app. The editor is the dangerous spot:
 * ProseMirror preventDefaults dragover (making itself a valid target) but
 * not a file drop, which would otherwise navigate the tab to the file.
 *
 * Pastes without files and drags that don't carry files are left completely
 * alone (no preventDefault) so text paste and in-page text drags into the
 * editor keep working.
 */

const isFileDrag = (e: DragEvent) => {
	const types = e.dataTransfer?.types;
	return !!types && Array.from(types).includes('Files');
};
export const useImagePasteAndDrop = ({
	enabled,
	onFiles,
	dropTargetRef,
}: {
	enabled: boolean;
	onFiles: (files: File[]) => void;
	dropTargetRef: RefObject<HTMLElement | null>;
}): { isDragActive: boolean } => {
	const [isDragActive, setIsDragActive] = useState(false);
	// dragenter/dragleave fire once per element the cursor crosses, not once
	// per drop target. Track the nesting depth so the overlay only hides when
	// the drag actually leaves the target instead of flickering between its
	// children.
	const dragDepthRef = useRef(0);
	const onFilesRef = useRef(onFiles);
	onFilesRef.current = onFiles;

	useEffect(() => {
		if (!enabled) return;

		const handlePaste = (e: ClipboardEvent) => {
			// Don't bail on e.defaultPrevented: the editor preventDefaults pastes
			// it processes, and a browser "Copy Image" clipboard carries both HTML
			// and a file — the file half must still attach.
			const files = e.clipboardData?.files;
			if (!files || files.length === 0) return;
			e.preventDefault();
			onFilesRef.current(Array.from(files));
		};

		window.addEventListener('paste', handlePaste);
		return () => window.removeEventListener('paste', handlePaste);
	}, [enabled]);

	// Window-level guard: preventDefault makes the window a valid (inert) drop
	// target, so a file dropped outside the prompt bar is discarded instead of
	// navigating the tab to the file. Real drop targets — the prompt bar
	// below, the reference-card DropZones — handle their events before these
	// bubbling listeners run, so the guard never steals a drop. Deliberately
	// not gated on `enabled`: the guard matters most exactly when attaching is
	// off (an upload in flight, a read-only chat).
	useEffect(() => {
		const guardDragOver = (e: DragEvent) => {
			if (isFileDrag(e)) e.preventDefault();
		};
		const guardDrop = (e: DragEvent) => {
			if (isFileDrag(e)) e.preventDefault();
		};

		window.addEventListener('dragover', guardDragOver);
		window.addEventListener('drop', guardDrop);
		return () => {
			window.removeEventListener('dragover', guardDragOver);
			window.removeEventListener('drop', guardDrop);
		};
	}, []);

	useEffect(() => {
		if (!enabled) return;
		const target = dropTargetRef.current;
		if (!target) return;

		const handleDragEnter = (e: DragEvent) => {
			// Only files raise the overlay; dragging text or in-page elements
			// must not.
			if (!isFileDrag(e)) return;
			dragDepthRef.current += 1;
			setIsDragActive(true);
		};

		const handleDragLeave = (e: DragEvent) => {
			if (!isFileDrag(e)) return;
			// Floor at 0: a dragenter that fired before these listeners attached
			// would otherwise drive the depth negative and wedge the overlay.
			if (dragDepthRef.current === 0) return;
			dragDepthRef.current -= 1;
			if (dragDepthRef.current === 0) {
				setIsDragActive(false);
			}
		};

		const handleDrop = (e: DragEvent) => {
			if (!isFileDrag(e)) return;
			if (dragDepthRef.current === 0) return;
			e.preventDefault();
			dragDepthRef.current = 0;
			setIsDragActive(false);
			const files = e.dataTransfer?.files;
			if (files && files.length > 0) {
				onFilesRef.current(Array.from(files));
			}
		};

		target.addEventListener('dragenter', handleDragEnter);
		target.addEventListener('dragleave', handleDragLeave);
		target.addEventListener('drop', handleDrop);
		return () => {
			target.removeEventListener('dragenter', handleDragEnter);
			target.removeEventListener('dragleave', handleDragLeave);
			target.removeEventListener('drop', handleDrop);
			dragDepthRef.current = 0;
		};
	}, [enabled, dropTargetRef]);

	// If the host disables paste/drop mid-drag (e.g. an upload starts), drop
	// the overlay rather than leaving it stuck on screen.
	useEffect(() => {
		if (!enabled) {
			dragDepthRef.current = 0;
			setIsDragActive(false);
		}
	}, [enabled]);

	return { isDragActive };
};
